import React from 'react';
import styled from 'styled-components';

import Input from '../../components/Input';
import Notification from '../../components/Notification';
import OptionsBuilder from './OptionsBuilder';
import {
  MEASUREMENT_UNITS,
  MEASUREMENT_UNIT_CATEGORIES,
  MEASUREMENT_UNIT_KEY,
  INVENTORY_ITEM_SOURCES,
  INVENTORY_LOT_SOURCES,
  INVENTORY_ITEM_SOURCE_KEY,
  INVENTORY_LOT_SOURCE_KEY,
  INVENTORY_LOT_QUESTION_KEY,
  getMeasurementUnit,
  getInventoryItemSource,
  getInventoryLotSource,
  getInventoryLotQuestionId,
  getInventoryLotQuestionCandidates,
  getConvertibleUnits
} from './shared';

// Settings for an inventory receiving or usage question: the unit the amount is entered in, where the item comes from
// and where the lot comes from.
//  - onMetaChange receives the meta keys to change (an empty value removes the key).
//  - onOptionsChange receives the pick list items.
//  - catalogRestriction is the "restrict the available catalog types" control, shown for a catalog item source.
//  - published is true for a saved question on a published form. The api then keeps the item source as it is, since
//    switching it would start new stock and strand the old balance, and only lets the unit move to one its amounts
//    convert into. The lot settings, list items and catalog restriction can still be changed.
// The lot settings are only shown for a receiving question. A usage question takes the lot from the stock it is
// used from, so there is nothing to set.
function InventoryQuestionConfig({form, question, catalog, validations = {}, optionValidations = {}, published = false, catalogRestriction, onMetaChange, onOptionsChange}) {
  const unit = getMeasurementUnit(question) || '';
  const itemSource = getInventoryItemSource(question) || '';
  const lotSource = getInventoryLotSource(question) || '';
  const lotQuestionId = getInventoryLotQuestionId(question) || '';
  const lotCandidates = getInventoryLotQuestionCandidates(form, question);
  // A lot question that is no longer offered (deleted, or changed to a type that can't be a lot) stays selectable,
  // so the select shows what is saved and validation can explain the problem.
  const missingLotQuestion = !!lotQuestionId && !lotCandidates.some(q => q.id === lotQuestionId);
  const receiving = question.type === 'INVENTORY_RECEIVING';
  const unitOptions = published ? getConvertibleUnits(unit) : MEASUREMENT_UNITS;
  // Says what the item is (what is received into inventory or used from it) and how it is answered.
  const itemSourceHelp = [
    receiving ? 'The item received into inventory.' : 'The item used from inventory.',
    published
      ? 'Can\'t be changed once the form is published.'
      : (INVENTORY_ITEM_SOURCES.filter(source => source.value === itemSource)[0] || {}).description
  ].filter(Boolean).join(' ');

  const handleLotSourceChange = e => {
    const value = e.target.value;
    onMetaChange({[INVENTORY_LOT_SOURCE_KEY]: value, ...(value !== 'QUESTION' && {[INVENTORY_LOT_QUESTION_KEY]: ''})});
  };

  return (
    <Container>
      {validations.question && (
        <Notification error mini>{validations.question}</Notification>
      )}

      <FieldGrid>
        <Input
          id={`inventory-unit-${question.id}`}
          type="select"
          label="Unit"
          required
          value={unit}
          onChange={e => onMetaChange({[MEASUREMENT_UNIT_KEY]: e.target.value})}
          invalid={!!validations.measurementUnit}
          validationText={validations.measurementUnit || ''}
          helpText={published ? 'Once published, only units it converts into.' : ''}>
          <option value="">Choose a unit</option>
          {MEASUREMENT_UNIT_CATEGORIES
            .filter(category => unitOptions.some(u => u.category === category.value))
            .map(category => (
              <optgroup key={category.value} label={category.label}>
                {unitOptions.filter(u => u.category === category.value).map(u => (
                  <option key={u.value} value={u.value}>{u.label}</option>
                ))}
              </optgroup>
            ))}
        </Input>
        <Input
          id={`inventory-item-source-${question.id}`}
          type="select"
          label="Item answer type"
          required
          disabled={published}
          value={itemSource}
          onChange={e => onMetaChange({[INVENTORY_ITEM_SOURCE_KEY]: e.target.value})}
          invalid={!!validations.itemSource}
          validationText={validations.itemSource || ''}
          helpText={itemSourceHelp}>
          <option value="">Choose one</option>
          {INVENTORY_ITEM_SOURCES.map(source => (
            <option key={source.value} value={source.value}>{source.label}</option>
          ))}
        </Input>
        {receiving && (
          <Input
            id={`inventory-lot-source-${question.id}`}
            type="select"
            label="Lot"
            required
            value={lotSource}
            onChange={handleLotSourceChange}
            invalid={!!validations.lotSource}
            validationText={validations.lotSource || ''}
            helpText={(INVENTORY_LOT_SOURCES.filter(source => source.value === lotSource)[0] || {}).description}>
            <option value="">Choose one</option>
            {INVENTORY_LOT_SOURCES.map(source => (
              <option key={source.value} value={source.value}>{source.label}</option>
            ))}
          </Input>
        )}
        {receiving && lotSource === 'QUESTION' && (
          <Input
            id={`inventory-lot-question-${question.id}`}
            type="select"
            label="Lot question"
            required
            value={lotQuestionId}
            onChange={e => onMetaChange({[INVENTORY_LOT_QUESTION_KEY]: e.target.value})}
            invalid={!!validations.lotQuestionId}
            validationText={validations.lotQuestionId || ''}
            helpText={lotCandidates.length === 0 ? 'Add a text, number, code or single-select question to use as the lot.' : ''}>
            <option value="">Choose a question</option>
            {missingLotQuestion && (
              <option value={lotQuestionId}>(Unavailable question)</option>
            )}
            {lotCandidates.map(q => (
              <option key={q.id} value={q.id}>{q.text || 'Untitled question'}</option>
            ))}
          </Input>
        )}
      </FieldGrid>

      {itemSource === 'CATALOG' && (
        <>
          {catalog.length === 0 && (
            <Hint>Your catalog is empty. Add items to it so they can be picked here.</Hint>
          )}
          {catalogRestriction}
        </>
      )}

      {itemSource === 'PICKLIST' && (
        <div>
          <OptionsBuilder
            question={question}
            validations={optionValidations}
            noDefaultAnswers
            optionPlaceholder="Enter an option"
            onChange={onOptionsChange}
            onChangeDefaultAnswers={() => {}}
          />
          {validations.questionOptions && (
            <ValidationText>{validations.questionOptions}</ValidationText>
          )}
        </div>
      )}

      <Hint>
        {receiving
          ? 'Each submission adds the amount to inventory for its item and lot.'
          : 'Each submission takes the amount from inventory for its item and lot.'}
      </Hint>
    </Container>
  );
}

const Container = styled.div`
  padding: 1rem;
  background: #f5f5f5;
  border-radius: 4px;
  margin: 1rem 0;

  > * {
    margin-top: 0.75rem;
  }

  > *:first-child {
    margin-top: 0;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  white-space: normal;
`;

const Hint = styled.small`
  display: block;
  font-size: 0.875rem;
  color: #747474;
`;

const ValidationText = styled.small`
  display: block;
  font-size: 0.875rem;
  color: #dc3545;
  margin-top: 0.5rem;
`;

export default InventoryQuestionConfig;
